/**
 * aeterna/neural/semantics.ts — Deep Semantic Understanding
 *
 * The gap between "processing text" and "understanding a person."
 *
 * Most NLP systems operate at the word/sentence level. They miss:
 *
 * 1. **Sentiment Trajectory** — Not "is this message positive?" but
 *    "has the player been slowly getting sadder over 8 messages?"
 *    The *shape* of emotional change matters more than any single point.
 *
 * 2. **Silence Modeling** — What does it mean when the player stops
 *    talking? Comfortable silence? Disengagement? Thinking? The gap
 *    between messages IS information. Most systems ignore it.
 *
 * 3. **Metaphor Detection** — "My heart is heavy" is not about cardiology.
 *    "This room feels cold" when the temperature is fine. Figurative
 *    language is the most human thing in conversation. Detecting it
 *    (even crudely) prevents the Aeterna from responding literally
 *    to poetic speech.
 *
 * 4. **Pronoun/Referent Tracking** — "They said that." Who is "they"?
 *    Without coreference, the Aeterna can't follow stories.
 *    We track a lightweight referent stack.
 *
 * 5. **Semantic Drift** — Does the player's vocabulary change over weeks?
 *    Are they using more complex language? More emotional words? This is
 *    long-term personality modeling from pure text statistics.
 *
 * 6. **Repetition Detection** — The player keeps saying "I'm fine."
 *    The third time means something different than the first.
 *    Repeated phrases are emotional tells.
 *
 * 7. **Question Depth Analysis** — "What's for dinner?" vs "Why do we
 *    exist?" Both are questions. The depth, abstraction, and emotional
 *    weight are completely different. The Aeterna must know this.
 *
 * Zero dependencies. Pattern-based. Sovereign.
 */

import { zeros, cosine, normalize, ema, vsub } from "./math.js";
import { tokenize } from "./embeddings.js";

// ─── Config ──────────────────────────────────────────────────────────────────

const MAX_SENTIMENT_HISTORY = 50;
const MAX_REFERENT_STACK = 8;
const SILENCE_SHORT = 5_000;    // 5s  — thinking pause
const SILENCE_MEDIUM = 30_000;  // 30s — engagement question
const SILENCE_LONG = 120_000;   // 2m  — likely disengaged

// ─── Sentiment Trajectory ────────────────────────────────────────────────────

export interface SentimentPoint {
  valence: number;    // -1 (negative) to +1 (positive)
  arousal: number;    // 0 (calm) to 1 (activated)
  timestamp: number;
}

export type SentimentShape =
  | "stable_positive"    // consistently good
  | "stable_negative"    // consistently sad
  | "rising"             // getting happier
  | "falling"            // getting sadder
  | "volatile"           // swinging between extremes
  | "plateau"            // flat, neither good nor bad
  | "recovery"           // was sad, now improving
  | "spiral"             // getting progressively worse
  | "neutral";

class SentimentTracker {
  private history: SentimentPoint[] = [];

  record(valence: number, arousal: number): void {
    this.history.push({ valence, arousal, timestamp: Date.now() });
    if (this.history.length > MAX_SENTIMENT_HISTORY) this.history.shift();
  }

  /** Detect the shape of the sentiment trajectory */
  getShape(): SentimentShape {
    const h = this.history;
    if (h.length < 4) return "neutral";

    const recent = h.slice(-5);
    const older = h.slice(-10, -5);
    if (older.length === 0) return "neutral";

    const recentMean = recent.reduce((s, p) => s + p.valence, 0) / recent.length;
    const olderMean = older.reduce((s, p) => s + p.valence, 0) / older.length;

    // Volatility: standard deviation of recent valences
    const recentVar = recent.reduce((s, p) => s + (p.valence - recentMean) ** 2, 0) / recent.length;
    const volatility = Math.sqrt(recentVar);

    const diff = recentMean - olderMean;

    if (volatility > 0.4) return "volatile";
    if (diff > 0.25 && olderMean < -0.1) return "recovery";
    if (diff < -0.15 && recentMean < olderMean) {
      return recentMean < -0.3 ? "spiral" : "falling";
    }
    if (diff > 0.15) return "rising";
    if (Math.abs(recentMean) < 0.15 && volatility < 0.15) return "plateau";
    if (recentMean > 0.2) return "stable_positive";
    if (recentMean < -0.2) return "stable_negative";
    return "neutral";
  }

  /** Derivative: how fast is sentiment changing? */
  getDerivative(): number {
    const h = this.history;
    if (h.length < 3) return 0;
    const last3 = h.slice(-3);
    return (last3[2].valence - last3[0].valence) / 2;
  }

  /** Overall arousal trend */
  getArousalTrend(): number {
    const h = this.history;
    if (h.length < 4) return 0.5;
    const recent = h.slice(-3).reduce((s, p) => s + p.arousal, 0) / 3;
    return recent;
  }

  getHistory(): SentimentPoint[] { return this.history; }
}

// ─── Silence Modeling ────────────────────────────────────────────────────────

export type SilenceType =
  | "thinking"       // short pause, probably composing
  | "comfortable"    // medium gap, context-dependent
  | "disengaged"     // long gap, player may have left
  | "processing"     // after heavy emotional content
  | "none";          // just arrived, no gap yet

class SilenceModel {
  private lastMessageTime = 0;
  private lastMessageValence = 0;
  private gapHistory: number[] = [];

  recordMessage(valence: number): void {
    const now = Date.now();
    if (this.lastMessageTime > 0) {
      this.gapHistory.push(now - this.lastMessageTime);
      if (this.gapHistory.length > 20) this.gapHistory.shift();
    }
    this.lastMessageTime = now;
    this.lastMessageValence = valence;
  }

  /** Classify the current silence */
  classifySilence(): SilenceType {
    if (this.lastMessageTime === 0) return "none";

    const gap = Date.now() - this.lastMessageTime;

    if (gap < SILENCE_SHORT) return "thinking";

    // After emotionally heavy content, longer silence = processing
    if (Math.abs(this.lastMessageValence) > 0.5 && gap < SILENCE_MEDIUM) {
      return "processing";
    }

    if (gap < SILENCE_MEDIUM) return "comfortable";
    if (gap < SILENCE_LONG) return "comfortable"; // still could be reading
    return "disengaged";
  }

  /** Average gap between messages (player's natural cadence) */
  getAverageGap(): number {
    if (this.gapHistory.length === 0) return 0;
    return this.gapHistory.reduce((a, b) => a + b) / this.gapHistory.length;
  }

  /** Is this message faster than usual? (player is excited/engaged) */
  isFasterThanUsual(): boolean {
    if (this.gapHistory.length < 3) return false;
    const avg = this.getAverageGap();
    const lastGap = this.gapHistory[this.gapHistory.length - 1];
    return lastGap < avg * 0.5;
  }

  /** Is this message slower than usual? (player is disengaging or thinking) */
  isSlowerThanUsual(): boolean {
    if (this.gapHistory.length < 3) return false;
    const avg = this.getAverageGap();
    const lastGap = this.gapHistory[this.gapHistory.length - 1];
    return lastGap > avg * 2.0;
  }
}

// ─── Metaphor Detection ──────────────────────────────────────────────────────
// Pattern-based detection of figurative language.
// Not trying to be comprehensive — just enough to prevent literal responses.

interface MetaphorPattern {
  pattern: RegExp;
  category: "emotional_body" | "spatial_emotion" | "temperature" | "weight" | "light_dark" | "journey" | "container";
  literal: string;
  figurative: string;
}

const METAPHOR_PATTERNS: MetaphorPattern[] = [
  // Body → Emotion
  { pattern: /heart\s+(is\s+)?(heavy|breaking|aching|sinking|full|warm)/i, category: "emotional_body", literal: "cardiac", figurative: "emotional pain/joy" },
  { pattern: /stomach\s+(is\s+)?(in knots|churning|dropping|sinking)/i, category: "emotional_body", literal: "digestive", figurative: "anxiety" },
  { pattern: /weight\s+(on|off)\s+(my|the)\s+shoulders/i, category: "weight", literal: "physical weight", figurative: "responsibility/relief" },
  { pattern: /lump\s+in\s+(my|the)\s+throat/i, category: "emotional_body", literal: "physical lump", figurative: "suppressed emotion" },
  // Temperature → Emotion
  { pattern: /(feels?|seems?)\s+(cold|frozen|icy|chilly)/i, category: "temperature", literal: "temperature", figurative: "emotional distance" },
  { pattern: /(burning|boiling|heated|on fire)\s+(inside|with|up)/i, category: "temperature", literal: "temperature", figurative: "anger/passion" },
  // Light/Dark → Mood
  { pattern: /(dark|dim|shadow|gloomy|bleak)\s+(place|time|days?|period|mood)/i, category: "light_dark", literal: "illumination", figurative: "depression/difficulty" },
  { pattern: /(bright|light|sunny|glowing)\s+(future|side|spot|day)/i, category: "light_dark", literal: "illumination", figurative: "optimism/hope" },
  // Spatial → Emotional
  { pattern: /feel(s?|ing)?\s+(lost|stuck|trapped|cornered|adrift)/i, category: "spatial_emotion", literal: "location", figurative: "emotional state" },
  { pattern: /at\s+a\s+crossroads/i, category: "journey", literal: "road intersection", figurative: "decision point" },
  { pattern: /hit\s+(a\s+)?(wall|dead end|rock bottom)/i, category: "journey", literal: "physical obstacle", figurative: "feeling blocked" },
  // Container → Emotion
  { pattern: /(bottl|hold|keep)(ed|ing)?\s+(it|everything|feelings?)\s+(in|inside|down)/i, category: "container", literal: "physical container", figurative: "emotional suppression" },
  { pattern: /(overflow|burst|erupt|explod)(ing|ed)?\s+(with|from)?/i, category: "container", literal: "physical overflow", figurative: "emotional overwhelm" },
  // Weight → Importance
  { pattern: /carry(ing)?\s+(a lot|so much|the weight|this burden)/i, category: "weight", literal: "physical carrying", figurative: "emotional burden" },
  { pattern: /let\s+(it|that|this)\s+go/i, category: "weight", literal: "physical release", figurative: "emotional release" },
];

export interface MetaphorResult {
  detected: boolean;
  category: string;
  figurativeMeaning: string;
  count: number;
}

function detectMetaphors(text: string): MetaphorResult {
  let count = 0;
  let lastCategory = "";
  let lastMeaning = "";

  for (const mp of METAPHOR_PATTERNS) {
    if (mp.pattern.test(text)) {
      count++;
      lastCategory = mp.category;
      lastMeaning = mp.figurative;
    }
  }

  return {
    detected: count > 0,
    category: lastCategory,
    figurativeMeaning: lastMeaning,
    count,
  };
}

// ─── Pronoun / Referent Tracking ─────────────────────────────────────────────
// Lightweight coreference: track who "they", "she", "he", "it" refer to.

interface Referent {
  pronoun: string;
  likelyEntity: string;
  confidence: number;
  firstMention: number; // message index
}

class ReferentTracker {
  private stack: Referent[] = [];
  private messageIndex = 0;

  /** Extract and track referents from a message */
  update(text: string): void {
    this.messageIndex++;
    const lower = text.toLowerCase();
    const tokens = lower.split(/\s+/);

    // Look for proper nouns or descriptive noun phrases before pronouns
    // Heuristic: Title Case words (first upper, rest lower) in original text.
    // Must NOT match ALL-CAPS (like "WHY WON'T ANYTHING") — that's shouting, not names.
    const words = text.split(/\s+/);
    const entities: string[] = [];
    for (let i = 1; i < words.length; i++) {
      const w = words[i];
      if (w.length > 1 && /^[A-Z][a-z]/.test(w) && !/^(I|I'm|I'll|I've|I'd)$/.test(w)) {
        entities.push(w);
      }
    }

    // Named entities become referent candidates
    for (const entity of entities) {
      this.pushReferent("they", entity); // default to "they" for named entities
    }

    // Track relational nouns as referents
    const relationalPatterns: Array<[RegExp, string]> = [
      [/my\s+(mom|mother|mum|mama)/i, "mother"],
      [/my\s+(dad|father|papa)/i, "father"],
      [/my\s+(sister|brother|sibling)/i, "$1"],
      [/my\s+(friend|best friend|buddy|mate)/i, "friend"],
      [/my\s+(partner|boyfriend|girlfriend|husband|wife|spouse)/i, "partner"],
      [/my\s+(dog|cat|pet|puppy|kitten)/i, "pet"],
      [/my\s+(boss|manager|teacher|professor)/i, "$1"],
      [/my\s+(child|son|daughter|kid|baby)/i, "child"],
    ];

    for (const [pattern, label] of relationalPatterns) {
      const match = text.match(pattern);
      if (match) {
        const actualLabel = label.startsWith("$") ? (match[1] ?? label) : label;
        this.pushReferent("they", actualLabel);
      }
    }
  }

  private pushReferent(pronoun: string, entity: string): void {
    // Check if already in stack
    const existing = this.stack.find(r => r.likelyEntity.toLowerCase() === entity.toLowerCase());
    if (existing) {
      existing.confidence = Math.min(1, existing.confidence + 0.2);
      return;
    }

    this.stack.push({
      pronoun,
      likelyEntity: entity,
      confidence: 0.5,
      firstMention: this.messageIndex,
    });

    // Keep stack bounded
    if (this.stack.length > MAX_REFERENT_STACK) {
      this.stack.shift();
    }
  }

  /** Who is "they" most likely referring to? */
  resolve(pronoun: string): string | null {
    const candidates = this.stack.filter(r => r.pronoun === pronoun);
    if (candidates.length === 0) return null;
    // Most recent, highest confidence
    candidates.sort((a, b) => b.confidence - a.confidence || b.firstMention - a.firstMention);
    return candidates[0].likelyEntity;
  }

  /** Get all tracked referents, filtered to recent (within last 5 messages) */
  getReferents(): Referent[] {
    const recencyWindow = 5;
    return this.stack.filter(r => this.messageIndex - r.firstMention <= recencyWindow);
  }
}

// ─── Repetition Detection ────────────────────────────────────────────────────

class RepetitionDetector {
  private phraseHistory: Array<{ text: string; count: number; lastSeen: number }> = [];
  private messageIndex = 0;

  record(text: string): { isRepetition: boolean; count: number; phrase: string | null } {
    this.messageIndex++;
    const normalized = text.toLowerCase().trim().replace(/[^\w\s]/g, "");

    if (normalized.length < 3) return { isRepetition: false, count: 0, phrase: null };

    // Exact or near-match detection
    for (const entry of this.phraseHistory) {
      if (entry.text === normalized || this.isSimilar(entry.text, normalized)) {
        entry.count++;
        entry.lastSeen = this.messageIndex;
        return {
          isRepetition: entry.count >= 2,
          count: entry.count,
          phrase: text.slice(0, 50),
        };
      }
    }

    // New phrase
    this.phraseHistory.push({ text: normalized, count: 1, lastSeen: this.messageIndex });
    if (this.phraseHistory.length > 50) this.phraseHistory.shift();

    return { isRepetition: false, count: 1, phrase: null };
  }

  /** Check if two phrases are similar (shared token overlap > 70%) */
  private isSimilar(a: string, b: string): boolean {
    const tokensA = new Set(a.split(/\s+/));
    const tokensB = new Set(b.split(/\s+/));
    if (tokensA.size < 2 || tokensB.size < 2) return a === b;

    let overlap = 0;
    for (const t of tokensA) {
      if (tokensB.has(t)) overlap++;
    }
    const maxSize = Math.max(tokensA.size, tokensB.size);
    return overlap / maxSize > 0.7;
  }
}

// ─── Question Depth Analysis ─────────────────────────────────────────────────

export type QuestionDepth = "surface" | "practical" | "personal" | "philosophical" | "existential";

function analyzeQuestionDepth(text: string): { depth: QuestionDepth; score: number } {
  const lower = text.toLowerCase();
  const isQuestion = /\?|^(what|who|why|how|where|when|do|does|did|can|could|would|will|is|are|have|has)\b/i.test(text);
  if (!isQuestion) return { depth: "surface", score: 0 };

  // Existential: meaning of life, purpose, death, consciousness
  if (/\b(meaning|purpose|exist|death|die|conscious|real(ity)?|universe|infinite|nothing|soul|free will)\b/i.test(lower)) {
    return { depth: "existential", score: 1.0 };
  }

  // Philosophical: why things are, beliefs, truth, morality
  if (/\b(why\s+(do|does|is|are|would)|believe|think\s+about|truth|right|wrong|matter|important|fair|just)\b/i.test(lower)) {
    return { depth: "philosophical", score: 0.8 };
  }

  // Personal: about feelings, relationships, self
  if (/\b(feel|love|miss|lonely|happy|sad|angry|scared|wish|dream|remember|forget|hurt|trust|afraid)\b/i.test(lower)) {
    return { depth: "personal", score: 0.6 };
  }

  // Practical: how-to, plans, logistics
  if (/\b(how\s+(do|to|can|should)|where\s+(is|can|do)|when\s+(is|will|does))\b/i.test(lower)) {
    return { depth: "practical", score: 0.3 };
  }

  return { depth: "surface", score: 0.1 };
}

// ─── Semantic Drift Tracker ──────────────────────────────────────────────────
// Long-term vocabulary change detection across sessions.

class SemanticDriftTracker {
  /** Word frequency snapshots per session */
  private sessionFreqs: Map<string, number>[] = [];
  private currentSessionFreqs = new Map<string, number>();
  private totalWordsSeen = 0;

  recordMessage(text: string): void {
    const tokens = tokenize(text);
    for (const t of tokens) {
      this.currentSessionFreqs.set(t, (this.currentSessionFreqs.get(t) ?? 0) + 1);
      this.totalWordsSeen++;
    }
  }

  /** Snapshot current session and start tracking new one */
  endSession(): void {
    if (this.currentSessionFreqs.size > 0) {
      this.sessionFreqs.push(new Map(this.currentSessionFreqs));
      this.currentSessionFreqs = new Map();
    }
    // Keep last 20 sessions
    if (this.sessionFreqs.length > 20) this.sessionFreqs.shift();
  }

  /** Vocabulary richness: unique words / total words */
  getVocabularyRichness(): number {
    if (this.totalWordsSeen === 0) return 0;
    return this.currentSessionFreqs.size / this.totalWordsSeen;
  }

  /** Has vocabulary shifted between sessions? (cosine distance of word freq vectors) */
  getSemanticDrift(): number {
    if (this.sessionFreqs.length < 2) return 0;
    const prev = this.sessionFreqs[this.sessionFreqs.length - 1];
    const curr = this.currentSessionFreqs;

    // Build shared vocabulary
    const allWords = new Set([...prev.keys(), ...curr.keys()]);
    let dot = 0, normA = 0, normB = 0;
    for (const w of allWords) {
      const a = prev.get(w) ?? 0;
      const b = curr.get(w) ?? 0;
      dot += a * b;
      normA += a * a;
      normB += b * b;
    }
    const denom = Math.sqrt(normA) * Math.sqrt(normB);
    if (denom < 1e-10) return 0;
    return 1 - (dot / denom); // cosine distance
  }

  /** Is the player using more emotional language than before? */
  getEmotionalWordRatio(): number {
    const EMOTIONAL_WORDS = new Set([
      "feel", "love", "hate", "miss", "happy", "sad", "angry", "scared",
      "lonely", "hurt", "sorry", "beautiful", "wonderful", "terrible",
      "afraid", "hope", "wish", "dream", "trust", "betrayed", "grateful",
      "anxious", "worried", "excited", "proud", "ashamed", "guilty",
      "jealous", "confused", "overwhelmed", "peaceful", "content",
    ]);

    let total = 0, emotional = 0;
    for (const [word, count] of this.currentSessionFreqs) {
      total += count;
      if (EMOTIONAL_WORDS.has(word)) emotional += count;
    }
    return total > 0 ? emotional / total : 0;
  }

  serialize(): string {
    return JSON.stringify({
      sessions: this.sessionFreqs.map(m => Object.fromEntries(m)),
      totalWordsSeen: this.totalWordsSeen,
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    this.sessionFreqs = (d.sessions ?? []).map((s: Record<string, number>) => new Map(Object.entries(s)));
    this.totalWordsSeen = d.totalWordsSeen ?? 0;
  }
}

// ─── Contradiction Detection ──────────────────────────────────────────────────
// "I'm fine" + high tension = something is wrong.
// The player's words say one thing; the affect signal says another.
// This is the most important thing in all of therapeutic communication.

export interface ContradictionResult {
  /** Was a contradiction detected? */
  detected: boolean;
  /** Type of contradiction */
  type: "denial" | "minimizing" | "performing" | "deflecting" | "none";
  /** What the text declares */
  declared: string;
  /** What the affect suggests */
  observed: string;
  /** Confidence in the contradiction (0-1) */
  confidence: number;
}

/** Phrases that claim positive state */
const POSITIVE_DECLARATIONS = [
  /\b(i'm|i am|im)\s+(fine|good|great|ok|okay|alright|happy|doing well)\b/i,
  /\b(everything'?s?|things? (are|is))\s+(fine|good|great|ok|okay|alright|normal)\b/i,
  /\b(no worries|don'?t worry|it'?s? nothing|no big deal|whatever|doesn'?t matter)\b/i,
  /\b(i don'?t (care|mind)|it'?s? cool|all good|no problem)\b/i,
];

/** Phrases that claim negative state */
const NEGATIVE_DECLARATIONS = [
  /\b(i'm|i am|im)\s+(sad|angry|upset|frustrated|annoyed|miserable|depressed|tired of)\b/i,
  /\b(i (hate|can'?t stand|despise|loathe))\b/i,
  /\b(everything'?s?|things? (are|is))\s+(terrible|awful|bad|horrible|wrong)\b/i,
];

function detectContradiction(
  text: string,
  valence: number,
  arousal: number,
  tension: number,
): ContradictionResult {
  const noResult: ContradictionResult = {
    detected: false, type: "none", declared: "", observed: "", confidence: 0,
  };

  // Check for positive declaration + negative affect
  for (const pat of POSITIVE_DECLARATIONS) {
    if (pat.test(text)) {
      if (tension > 0.6 && valence < -0.1) {
        return {
          detected: true,
          type: "denial",
          declared: "claims to be fine",
          observed: "high tension and negative affect",
          confidence: Math.min(1, tension * 0.8 + Math.abs(valence) * 0.4),
        };
      }
      if (arousal > 0.7 && valence < 0.1) {
        return {
          detected: true,
          type: "performing",
          declared: "claims to be fine",
          observed: "unusually activated — may be forcing it",
          confidence: arousal * 0.6,
        };
      }
    }
  }

  // Check for negative declaration + positive affect
  for (const pat of NEGATIVE_DECLARATIONS) {
    if (pat.test(text)) {
      if (valence > 0.3 && tension < 0.3) {
        return {
          detected: true,
          type: "performing",
          declared: "claims distress",
          observed: "affect reads positive — may be venting rather than suffering",
          confidence: 0.5,
        };
      }
    }
  }

  // Minimizing: short dismissive response with underlying tension
  const lower = text.toLowerCase().trim();
  const dismissive = ["ok", "okay", "sure", "fine", "whatever", "nm", "nvm",
                       "nah", "meh", "idk", "idc", "k"];
  if (dismissive.includes(lower) && (tension > 0.4 || valence < -0.2)) {
    return {
      detected: true,
      type: "minimizing",
      declared: "dismissive/short response",
      observed: "underlying tension or negativity",
      confidence: Math.min(1, tension * 0.6 + Math.abs(valence) * 0.3),
    };
  }

  return noResult;
}

// ─── Composite Semantics Engine ──────────────────────────────────────────────

export interface SemanticState {
  /** Shape of the emotional trajectory */
  sentimentShape: SentimentShape;
  /** Sentiment derivative (positive = improving) */
  sentimentDerivative: number;
  /** Arousal level (0=calm, 1=activated) */
  arousal: number;

  /** Current silence classification */
  silenceType: SilenceType;
  /** Is the player typing faster than usual? */
  isEngaged: boolean;
  /** Is the player disengaging? */
  isDisengaging: boolean;

  /** Metaphor detection result */
  metaphor: MetaphorResult;

  /** Known referents ("they" → "sister") */
  referents: Array<{ pronoun: string; entity: string }>;

  /** Repetition flag */
  isRepetition: boolean;
  /** How many times this phrase has been said */
  repetitionCount: number;
  /** The repeated phrase (if any) */
  repeatedPhrase: string | null;

  /** Question depth analysis */
  questionDepth: QuestionDepth;
  /** Question depth score (0-1) */
  questionScore: number;

  /** Vocabulary richness (this session) */
  vocabularyRichness: number;
  /** Semantic drift from last session */
  semanticDrift: number;
  /** Ratio of emotional words */
  emotionalWordRatio: number;

  /** Contradiction detection: declared vs observed emotional state */
  contradiction: ContradictionResult;

  /** Within-topic semantic drift: conversation drifting without explicit topic switch */
  topicDrift: TopicDriftResult;
}

// ─── Within-Topic Semantic Drift ──────────────────────────────────────────────
// Conversations drift *within* topics without ever explicitly switching.
// You start talking about room design and end up talking about feeling like
// nothing belongs to you, through a chain of small steps where each was
// related to the last. DST detects topic switches. This detects the drift.

export interface TopicDriftResult {
  /** Has the conversation drifted significantly from where the topic started? */
  hasDrifted: boolean;
  /** Magnitude of drift (0 = same spot, 1 = completely different) */
  driftMagnitude: number;
  /** Direction vector of the drift in embedding space (64-dim), or null */
  trajectory: Float32Array | null;
  /** Number of turns since topic start */
  turnsSinceTopicStart: number;
}

export class SemanticsEngine {
  private sentiment: SentimentTracker;
  private silence: SilenceModel;
  private referents: ReferentTracker;
  private repetition: RepetitionDetector;
  private drift: SemanticDriftTracker;

  // Within-topic drift tracking
  private topicStartEmbed: Float32Array | null = null;
  private topicTurnCount = 0;
  /** Drift threshold: cosine distance above which we flag drift */
  private static readonly DRIFT_THRESHOLD = 0.45;
  /** Minimum turns before drift detection is meaningful */
  private static readonly DRIFT_MIN_TURNS = 4;

  constructor() {
    this.sentiment = new SentimentTracker();
    this.silence = new SilenceModel();
    this.referents = new ReferentTracker();
    this.repetition = new RepetitionDetector();
    this.drift = new SemanticDriftTracker();
  }

  /**
   * Full semantic analysis of a message.
   *
   * @param text — raw message text
   * @param valence — from affect analysis (-1 to +1)
   * @param arousal — from affect analysis (0 to 1)
   */
  /**
   * @param embedding  The 64-dim embedding of the current message (from embed()).
   *                   Used for within-topic drift detection. Pass null to skip.
   * @param topicChanged  True if DST detected a topic switch this turn.
   */
  analyze(text: string, valence: number, arousal: number, tension = 0,
          embedding: Float32Array | null = null, topicChanged = false): SemanticState {
    // Update all trackers
    this.sentiment.record(valence, arousal);
    this.silence.recordMessage(valence);
    this.referents.update(text);
    this.drift.recordMessage(text);

    const rep = this.repetition.record(text);
    const metaphor = detectMetaphors(text);
    const question = analyzeQuestionDepth(text);
    const contradiction = detectContradiction(text, valence, arousal, tension);
    const topicDrift = this.detectTopicDrift(embedding, topicChanged);

    return {
      sentimentShape: this.sentiment.getShape(),
      sentimentDerivative: this.sentiment.getDerivative(),
      arousal: this.sentiment.getArousalTrend(),

      silenceType: this.silence.classifySilence(),
      isEngaged: this.silence.isFasterThanUsual(),
      isDisengaging: this.silence.isSlowerThanUsual(),

      metaphor,

      referents: this.referents.getReferents().map(r => ({
        pronoun: r.pronoun,
        entity: r.likelyEntity,
      })),

      isRepetition: rep.isRepetition,
      repetitionCount: rep.count,
      repeatedPhrase: rep.phrase,

      questionDepth: question.depth,
      questionScore: question.score,

      vocabularyRichness: this.drift.getVocabularyRichness(),
      semanticDrift: this.drift.getSemanticDrift(),
      emotionalWordRatio: this.drift.getEmotionalWordRatio(),

      contradiction,

      topicDrift,
    };
  }

  // ─── Within-Topic Drift ──────────────────────────────────────────────

  /**
   * Detect within-topic semantic drift.
   * Conversations drift without explicit topic switches: you start talking
   * about room design and end up talking about existential belonging.
   */
  private detectTopicDrift(embedding: Float32Array | null, topicChanged: boolean): TopicDriftResult {
    // Reset on explicit topic switch or if no embedding available
    if (topicChanged || !embedding) {
      if (embedding) {
        this.topicStartEmbed = new Float32Array(embedding);
      }
      this.topicTurnCount = 0;
      return { hasDrifted: false, driftMagnitude: 0, trajectory: null, turnsSinceTopicStart: 0 };
    }

    // First message in topic
    if (!this.topicStartEmbed) {
      this.topicStartEmbed = new Float32Array(embedding);
      this.topicTurnCount = 0;
      return { hasDrifted: false, driftMagnitude: 0, trajectory: null, turnsSinceTopicStart: 0 };
    }

    this.topicTurnCount++;

    // Too early for meaningful drift detection
    if (this.topicTurnCount < SemanticsEngine.DRIFT_MIN_TURNS) {
      return {
        hasDrifted: false,
        driftMagnitude: 1 - cosine(this.topicStartEmbed, embedding),
        trajectory: null,
        turnsSinceTopicStart: this.topicTurnCount,
      };
    }

    const driftDistance = 1 - cosine(this.topicStartEmbed, embedding);
    const hasDrifted = driftDistance > SemanticsEngine.DRIFT_THRESHOLD;

    // The drift trajectory: direction vector pointing from start to current
    const trajectory = hasDrifted
      ? vsub(embedding, this.topicStartEmbed)
      : null;

    return {
      hasDrifted,
      driftMagnitude: driftDistance,
      trajectory,
      turnsSinceTopicStart: this.topicTurnCount,
    };
  }

  /** Reset topic drift tracking (e.g., on session start) */
  resetTopicDrift(): void {
    this.topicStartEmbed = null;
    this.topicTurnCount = 0;
  }

  /** Build context fragment for EntityZero */
  buildContextFragment(state: SemanticState): string {
    const lines: string[] = [];

    // Sentiment trajectory
    if (state.sentimentShape !== "neutral" && state.sentimentShape !== "plateau") {
      lines.push(`Emotional trajectory: ${state.sentimentShape}`);
    }

    // Metaphor
    if (state.metaphor.detected) {
      lines.push(`Speaking figuratively (${state.metaphor.category}): means ${state.metaphor.figurativeMeaning}`);
    }

    // Repetition
    if (state.isRepetition && state.repetitionCount >= 3) {
      lines.push(`Repeated "${state.repeatedPhrase}" ${state.repetitionCount} times — this matters to them`);
    }

    // Referents
    if (state.referents.length > 0) {
      const refs = state.referents.slice(0, 3).map(r => `"${r.pronoun}" → ${r.entity}`).join(", ");
      lines.push(`Tracking: ${refs}`);
    }

    // Question depth
    if (state.questionDepth === "existential" || state.questionDepth === "philosophical") {
      lines.push(`Deep question (${state.questionDepth})`);
    }

    // Contradiction
    if (state.contradiction.detected) {
      lines.push(`Contradiction: ${state.contradiction.declared} but ${state.contradiction.observed} (${state.contradiction.type})`);
    }

    // Within-topic drift
    if (state.topicDrift.hasDrifted) {
      lines.push(
        `The conversation has drifted significantly from where it started ` +
        `(${state.topicDrift.turnsSinceTopicStart} turns, magnitude ${state.topicDrift.driftMagnitude.toFixed(2)}). ` +
        `The player may not be aware of the shift.`
      );
    }

    // Engagement
    if (state.isEngaged) {
      lines.push("Player is responding quickly — they're engaged");
    } else if (state.isDisengaging) {
      lines.push("Player is responding slowly — may be losing interest");
    }

    return lines.join("\n");
  }

  /** End session: snapshot drift tracker */
  endSession(): void {
    this.drift.endSession();
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      drift: this.drift.serialize(),
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.drift) this.drift.loadState(d.drift);
  }
}
